import { Component, Input, OnInit } from '@angular/core';
import { GameSettingsService } from '../game-settings.service';
import { environment } from '@env/environment';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material';
import { takeUntil } from 'rxjs/operators';
import { CustomCountComponent } from './custom-count/custom-count.component';
import { Subject } from 'rxjs';
import * as shuffle from "shuffle-array";
import { AdminApi } from '@app/shared/sdk';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';

@Component({
  selector: 'new-emoji-countdown-popup',
  templateUrl: './new-emoji-countdown-popup.component.html',
  styleUrls: ['./new-emoji-countdown-popup.component.scss']
})
export class NewEmojiCountdownPopupComponent implements OnInit {

  private arrowClicked = false;

  @Input() countDown: string;
  @Input() countDownTextAreaInput: string;
  @Input() countDownImage: string;
  @Input() countDownImageIsExpand: string;
  @Input() countDownEmoji: string;
  @Input() parentForm: FormGroup;
  @Input() emojisPerScreen: number;
  @Input() index: number;
  @Input() tabs: number;
  @Input() editedGameId: string;
  @Input() isRounded: boolean;
  
  s3BucketUrl: string = environment.s3BucketUrl;

  emojiImage: string = null;

  dialogRef: any;

  // Private
  private _unsubscribeAll: Subject<any>;

  constructor(
    private gameSettingsService: GameSettingsService, 
    private dialog: MatDialog, 
    private adminApi: AdminApi,
    private fuseSplashScreen: FuseSplashScreenService) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();
   }

  ngOnInit() {
  }

  getImageSrc(i, tabs, emojisPerScreen) {
    // i is the index of the current emoji being displayed, tabs is the current tab number and emojisPerScreen is the number of emojis per screen
    // index is calculated by adding i to tabs multiplied by emojisPerScreen
    const index = i + tabs * emojisPerScreen;
    // emojis is either the emojis from the extraEmojisSet with their emoji property mapped out, or just the extraEmojisSet
    let emojis = [
      ...this.gameSettingsService.reorganizeEmojisAndConcatenate(
        this.editedGameId,
        this.gameSettingsService.extraEmojisSet
      ),
    ];


    // the image source by concatenating the s3BucketUrl with the image property of the current emoji, after replacing the s3BucketUrl in the image property
    this.emojiImage = emojis[index] && emojis[index].image
    ? this.s3BucketUrl + emojis[index].image.replace(this.s3BucketUrl, "")
    : "";

    // return the emoji;
    return this.emojiImage;
  }

  // Handle the input value and ensure it is positive
  handleInputValue(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    let value = Number(inputElement.value);

    if (isNaN(value) || value < 0) {
      value = 0;
    }

    inputElement.value = value.toString();
  }

  dblClickNumberCount = ($event, index) => {
    if (!this.arrowClicked) {
      this.dialogRef = this.dialog.open(CustomCountComponent, {
        panelClass: "custom-count-dialog",
        height: "auto",
        width: "650px",
        data: {
          title: `Custom Number Count ${index + 1}`,
          count: this.parentForm.controls[this.countDown].value,
          emojiImage: this.emojiImage,
          type: 'count',
          isExpand: this.parentForm.controls[this.countDownImageIsExpand].value,
          countImage: this.parentForm.controls[this.countDownImage].value,
          index: this.index,
          textAreaInput: this.parentForm.controls[this.countDownTextAreaInput].value
        },
      });
      this.dialogRef
        .afterClosed()
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(async (response) => {
          if (response) {
            this.parentForm.controls[this.countDown].setValue(response.count);
            this.parentForm.controls[this.countDownImageIsExpand].setValue(response.countImage.isExpand);
            this.parentForm.controls[this.countDownTextAreaInput].setValue(response.textAreaCountDownInput);
            if (response.countImage.file !== null){
              await this.uploadImage(response.countImage.file);
            }
          }});
    } 
    this.arrowClicked = false;
  }

  preventArrowClick(event: MouseEvent) {
    if (event.target) {
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT') {
        // && target.type === 'number'
        const rect = target.getBoundingClientRect();
        const clickX = event.clientX - rect.left;

        // Check if the click was within the arrow button area
        if (clickX > target.offsetWidth - 20) {
          this.arrowClicked = true;
        }
      }
    }
  }

  uploadImage = async (file) => {
    this.fuseSplashScreen.show();
    const uploadPath = environment.mainContainer + "/" + "count-down-image";
    const adminId = this.adminApi.getCurrentToken().id;
    try {
      const response: any = await this.gameSettingsService.uploadImage(file, uploadPath, adminId).toPromise();
      if (!response) {
        this.fuseSplashScreen.hide();
        throw new Error('Cannot Upload Color Wheel Loader Image, Please Try Again!');
      }
      this.parentForm.controls[this.countDownImage].setValue(response.result.files["file"][0]["providerResponse"].name);
      this.fuseSplashScreen.hide();
    } catch (error) {
      this.fuseSplashScreen.hide();
      throw Error(error.message);
    }
  }

  dblClickEmojiCount = (index) => {
    const count = this.parentForm.controls[this.countDown].value;
    let emojis = [];
    const countDownEmojiValue = this.parentForm.controls[this.countDownEmoji].value;
    if (countDownEmojiValue !== null && countDownEmojiValue.length !== 0) {
      emojis = countDownEmojiValue;
    }
        
    this.dialogRef = this.dialog.open(CustomCountComponent, {
      panelClass: "custom-count-dialog",
      height: "auto",
      width: "650px",
      data: {
        title: `Custom Emoji Count ${index + 1}`,
        count: count,
        emojis: emojis,
        emojiImage: this.emojiImage,
        type: 'emoji',
        isExpand: this.parentForm.controls[this.countDownImageIsExpand].value,
        countImage: this.parentForm.controls[this.countDownImage].value,
        index: this.index,
      },
    });
  
    this.dialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(async (response) => {
        if (response) {
          this.parentForm.controls[this.countDown].setValue(response.count);
          this.parentForm.controls[this.countDownImageIsExpand].setValue(response.countImage.isExpand);
          if (response.countImage.file !== null){
            await this.uploadImage(response.countImage.file);
          }

          if (response.emojis !== undefined && response.emojis.length !== 0) 
            this.parentForm.controls[this.countDownEmoji].setValue(response.emojis);
        }
      });
  }
}
